import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DashboardLayout from "@/components/DashboardLayout";
import { EmptyState } from "@/components/StateViews";
import { SignedLink } from "@/lib/signedFile";
import { toast } from "sonner";
import { Loader2, LifeBuoy, Plus, X, Send, CheckCircle2, MessageCircle } from "lucide-react";

const REQUEST_TYPES = [
  "مشكلة تقنية","مساعدة في استخدام المنصة","استفسار عن طلب أو فرصة",
  "مشكلة في ورشة أو Microsoft Teams","مشكلة في الحساب أو تسجيل الدخول",
  "ملاحظة أو اقتراح","بلاغ متعلق بالخصوصية أو الأمان","أخرى",
];
const SUBJECTS = [
  "الحساب وتسجيل الدخول","الملف الشخصي والبيانات","التسجيل والتقديم","الفرص والبرامج",
  "المهام والملفات","الورش وMicrosoft Teams","الإشعارات","الشهادات","مشكلة في عرض الصفحة","أخرى",
];
const IMPACTS = [
  "تمنعني من استخدام المنصة","تمنعني من إكمال إجراء محدد","توجد مشكلة لكن يمكنني المتابعة","استفسار فقط",
];
const CONTACT_METHODS = ["البريد الإلكتروني","رقم الجوال","الرد داخل المنصة"];

const STATUS_TONES = {
  new: "bg-blue-50 text-blue-700 border-blue-100",
  under_review: "bg-amber-50 text-amber-700 border-amber-100",
  in_progress: "bg-indigo-50 text-indigo-700 border-indigo-100",
  awaiting_user: "bg-orange-50 text-orange-700 border-orange-100",
  resolved: "bg-emerald-50 text-emerald-700 border-emerald-100",
  closed: "bg-slate-100 text-slate-600 border-slate-200",
  reopened: "bg-rose-50 text-rose-700 border-rose-100",
};

export default function Support() {
  const [searchParams] = useSearchParams();
  const initialRelatedPage = searchParams.get("related_page") || "";
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [opened, setOpened] = useState(null);
  const [successNo, setSuccessNo] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/support/tickets");
      setTickets(data.tickets || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (searchParams.get("new") === "1") setCreating(true); }, [searchParams]);

  return (
    <DashboardLayout>
      <div data-testid="support-page">
        <div className="mb-8 flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-teal-700">مركز الدعم</div>
            <h1 className="mt-2 text-2xl font-heading font-extrabold text-slate-900">تذاكري وطلبات المساعدة</h1>
            <p className="mt-2 text-slate-600 max-w-3xl">افتح تذكرة دعم لأي مشكلة أو استفسار، وسنرد عليك خلال يوم عمل واحد.</p>
          </div>
          <button onClick={() => setCreating(true)} className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-teal-700 text-white font-semibold hover:bg-teal-600" data-testid="open-new-ticket">
            <Plus className="h-4 w-4" /> فتح تذكرة جديدة
          </button>
        </div>

        {loading ? (
          <div className="grid place-items-center py-16"><Loader2 className="h-8 w-8 animate-spin text-teal-700" /></div>
        ) : tickets.length === 0 ? (
          <EmptyState icon={LifeBuoy} title="لا توجد تذاكر بعد" description="افتح تذكرتك الأولى إن احتجت أي مساعدة." testid="support-empty" />
        ) : (
          <div className="grid gap-3">
            {tickets.map((t) => (
              <button key={t.id} onClick={() => setOpened(t)} className="p-4 rounded-2xl bg-white border border-cream-200 card-shadow text-start hover:border-teal-500 transition-colors" data-testid={`ticket-${t.ticket_no}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="text-xs text-slate-500 arabic-numerals" dir="ltr">{t.ticket_no}</div>
                    <div className="font-heading font-bold text-slate-900 mt-0.5">{t.title}</div>
                    <div className="text-xs text-slate-500 mt-1">{t.request_type} · {t.subject}</div>
                  </div>
                  <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold border ${STATUS_TONES[t.status] || "bg-slate-100"}`}>{t.status_label}</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {creating && <NewTicketModal user={user} initialRelatedPage={initialRelatedPage} onClose={() => setCreating(false)} onCreated={(no) => { setCreating(false); setSuccessNo(no); load(); }} />}
      {successNo && <SuccessModal ticketNo={successNo} onClose={() => setSuccessNo(null)} />}
      {opened && <TicketDetailModal ticketId={opened.id} onClose={() => setOpened(null)} onChanged={load} isAdmin={false} />}
    </DashboardLayout>
  );
}

function NewTicketModal({ user, initialRelatedPage, onClose, onCreated }) {
  const [v, setV] = useState({ request_type: "", subject: "", title: "", description_what: "", description_expected: "", description_when: "", impact: "", related_page: initialRelatedPage || "", attachment_url: "", preferred_contact: "" });
  const [saving, setSaving] = useState(false);
  const set = (k, x) => setV((s) => ({ ...s, [k]: x }));
  const ok = v.request_type && v.subject && v.title && v.description_what && v.impact && v.preferred_contact;

  const submit = async () => {
    if (!ok) return;
    setSaving(true);
    try {
      const { data } = await api.post("/support/tickets", v);
      onCreated(data.ticket_no);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData(); fd.append("file", f); fd.append("context", "support_attachment");
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("attachment_url", data.file_id);
      toast.success("تم رفع الصورة");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <ModalShell title="فتح تذكرة دعم جديدة" onClose={onClose} testid="new-ticket-modal" wide>
      <div className="p-4 overflow-y-auto space-y-4">
        <div className="p-3 rounded-lg bg-cream-50 border border-cream-200 text-xs text-slate-600">
          سنستخدم بياناتك المسجّلة تلقائياً: <strong>{user?.name}</strong> · <span dir="ltr">{user?.email}</span>{user?.phone && <> · <span dir="ltr">{user.phone}</span></>}
        </div>
        <RadioGroup label="نوع الطلب" name="rt" options={REQUEST_TYPES} value={v.request_type} onChange={(x) => set("request_type", x)} />
        <RadioGroup label="موضوع المشكلة" name="sub" options={SUBJECTS} value={v.subject} onChange={(x) => set("subject", x)} />
        <Text label="عنوان التذكرة" placeholder="مثال: لا يظهر زر الانضمام إلى الورشة" value={v.title} onChange={(x) => set("title", x)} testid="tk-title" />
        <div className="grid gap-3 md:grid-cols-3">
          <Text label="ماذا حدث؟" value={v.description_what} onChange={(x) => set("description_what", x)} multiline testid="tk-what" />
          <Text label="ماذا كنت تتوقع؟" value={v.description_expected} onChange={(x) => set("description_expected", x)} multiline testid="tk-expected" />
          <Text label="متى ظهرت المشكلة؟" value={v.description_when} onChange={(x) => set("description_when", x)} multiline testid="tk-when" />
        </div>
        <RadioGroup label="مدى تأثير المشكلة" name="imp" options={IMPACTS} value={v.impact} onChange={(x) => set("impact", x)} />
        <Text label="الصفحة المرتبطة بالمشكلة (اختياري)" value={v.related_page} onChange={(x) => set("related_page", x)} placeholder="/dashboard/sessions" dir="ltr" testid="tk-page" />
        <div>
          <div className="text-sm font-medium text-slate-700 mb-2">صورة شاشة (اختياري)</div>
          <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" onChange={upload} data-testid="tk-attach" />
          {v.attachment_url && <div className="mt-2 text-xs text-emerald-700 flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> تم رفع الصورة</div>}
        </div>
        <RadioGroup label="طريقة التواصل المفضلة" name="pc" options={CONTACT_METHODS} value={v.preferred_contact} onChange={(x) => set("preferred_contact", x)} />
      </div>
      <div className="px-4 py-3 border-t border-slate-200 flex justify-end gap-2">
        <button onClick={onClose} className="px-4 py-2 rounded-lg text-slate-700 hover:bg-slate-100">إلغاء</button>
        <button onClick={submit} disabled={!ok || saving} className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-teal-700 text-white font-semibold hover:bg-teal-600 disabled:opacity-60" data-testid="tk-submit">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} إرسال التذكرة
        </button>
      </div>
    </ModalShell>
  );
}

function SuccessModal({ ticketNo, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl border border-slate-200 p-5 text-center" data-testid="ticket-success">
        <div className="mx-auto h-16 w-16 rounded-full bg-emerald-100 grid place-items-center mb-4">
          <CheckCircle2 className="h-9 w-9 text-emerald-700" />
        </div>
        <h2 className="text-2xl font-heading font-black text-slate-900">تم فتح تذكرتك بنجاح</h2>
        <div className="mt-3 text-slate-600">رقم التذكرة:</div>
        <div className="mt-1 text-2xl font-mono font-black text-teal-700 arabic-numerals" dir="ltr">{ticketNo}</div>
        <p className="mt-4 text-sm text-slate-500">يمكنك متابعة حالتها والرد على فريق الدعم من مركز الدعم.</p>
        <button onClick={onClose} className="mt-6 w-full px-5 py-3 rounded-lg bg-teal-700 text-white font-semibold hover:bg-teal-600">حسناً</button>
      </div>
    </div>
  );
}

export function TicketDetailModal({ ticketId, onClose, onChanged, isAdmin }) {
  const [t, setT] = useState(null);
  const [reply, setReply] = useState("");
  const [saving, setSaving] = useState(false);
  const [newStatus, setNewStatus] = useState("");

  const load = async () => {
    try {
      const url = isAdmin ? `/support/tickets/${ticketId}` : `/support/tickets/${ticketId}`;
      const { data } = await api.get(url);
      setT(data.ticket);
      setNewStatus(data.ticket.status);
    } catch (e) { toast.error(formatApiError(e)); onClose(); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [ticketId]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const send = async () => {
    if (!reply.trim()) return;
    setSaving(true);
    try {
      await api.post(`/support/tickets/${ticketId}/reply`, { body: reply });
      setReply("");
      load(); if (onChanged) onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const changeStatus = async (s) => {
    setSaving(true);
    try {
      await api.patch(`/admin/support/tickets/${ticketId}`, { status: s });
      toast.success("تم تحديث الحالة");
      setNewStatus(s); load(); if (onChanged) onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <ModalShell title={t ? `${t.ticket_no} — ${t.title}` : "تذكرة"} onClose={onClose} testid="ticket-detail" wide>
      {!t ? (
        <div className="p-12 grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-teal-700" /></div>
      ) : (
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold border ${STATUS_TONES[t.status] || "bg-slate-100"}`}>{t.status_label}</span>
            <span className="text-xs text-slate-500">{t.request_type} · {t.subject}</span>
          </div>
          {isAdmin && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-slate-600">تحديث الحالة:</span>
              {Object.entries({new:"جديدة",under_review:"قيد المراجعة",in_progress:"قيد المعالجة",awaiting_user:"بانتظار رد المستخدم",resolved:"تم الحل",closed:"مغلقة",reopened:"أعيد فتحها"}).map(([k,l]) => (
                <button key={k} onClick={() => changeStatus(k)} disabled={saving || newStatus === k} className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${newStatus === k ? "bg-teal-700 text-white border-teal-700" : "bg-white text-slate-700 border-slate-200 hover:border-teal-500"}`} data-testid={`status-${k}`}>{l}</button>
              ))}
            </div>
          )}
          <div className="grid md:grid-cols-3 gap-3 text-sm">
            <Meta label="مقدّم الطلب" value={`${t.user_name} — ${t.user_role}`} />
            <Meta label="البريد" value={t.user_email} dir="ltr" />
            <Meta label="الجوال" value={t.user_phone || "—"} dir="ltr" />
            <Meta label="التأثير" value={t.impact} />
            <Meta label="طريقة التواصل" value={t.preferred_contact} />
            <Meta label="الصفحة" value={t.related_page || "—"} dir="ltr" />
          </div>
          <div className="p-4 rounded-xl bg-cream-50 border border-cream-200 space-y-2 text-sm">
            <div><strong>ماذا حدث:</strong> {t.description_what}</div>
            {t.description_expected && <div><strong>ما كان متوقعاً:</strong> {t.description_expected}</div>}
            {t.description_when && <div><strong>متى:</strong> {t.description_when}</div>}
          </div>
          {t.attachment_url && (
            <SignedLink fileId={t.attachment_url} className="text-sm text-teal-700 hover:underline">📎 المرفق</SignedLink>
          )}

          <div className="pt-2 border-t border-slate-200">
            <h4 className="font-heading font-bold text-slate-900 mb-3 flex items-center gap-2"><MessageCircle className="h-4 w-4" /> المحادثة</h4>
            <div className="space-y-2">
              {(t.replies || []).length === 0 && <div className="text-sm text-slate-500 py-3">لا توجد ردود بعد.</div>}
              {(t.replies || []).map((r, i) => (
                <div key={i} className={`p-3 rounded-lg text-sm ${r.is_admin ? "bg-teal-50 border border-teal-100" : "bg-slate-50 border border-slate-100"}`}>
                  <div className="text-xs text-slate-500 mb-1 flex items-center justify-between">
                    <span className="font-semibold">{r.is_admin ? "🛡 فريق الدعم" : r.from_name}</span>
                    <span>{r.created_at?.slice(0, 16).replace("T", " ")}</span>
                  </div>
                  <div className="whitespace-pre-wrap text-slate-800">{r.body}</div>
                </div>
              ))}
            </div>
            <div className="mt-4">
              <textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={3} placeholder="اكتب ردك..." className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none" data-testid="reply-input" />
              <button onClick={send} disabled={saving || !reply.trim()} className="mt-2 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-teal-700 text-white font-semibold hover:bg-teal-600 disabled:opacity-60" data-testid="reply-send">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} إرسال
              </button>
            </div>
          </div>
        </div>
      )}
    </ModalShell>
  );
}

function Meta({ label, value, dir }) {
  return (
    <div className="p-3 rounded-lg bg-white border border-cream-200">
      <div className="text-[10px] text-slate-500 font-semibold uppercase mb-1">{label}</div>
      <div dir={dir} className="text-sm text-slate-900 font-medium">{value}</div>
    </div>
  );
}

function ModalShell({ title, onClose, children, wide, testid }) {
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`w-full ${wide ? "max-w-4xl" : "max-w-md"} bg-white rounded-2xl shadow-2xl border border-slate-200 max-h-[92vh] flex flex-col`} data-testid={testid}>
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-heading font-bold text-slate-900 text-lg truncate">{title}</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100"><X className="h-5 w-5" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Text({ label, value, onChange, placeholder, multiline, testid, dir }) {
  const Comp = multiline ? "textarea" : "input";
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-700 mb-1.5">{label}</span>
      <Comp rows={multiline ? 3 : undefined} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} dir={dir}
        className="w-full rounded-lg border border-slate-300 bg-white px-4 py-2.5 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none" data-testid={testid} />
    </label>
  );
}

function RadioGroup({ label, options, value, onChange, name }) {
  return (
    <div>
      <div className="text-sm font-medium text-slate-700 mb-2">{label}</div>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => (
          <button key={o} type="button" onClick={() => onChange(o)} className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${value === o ? "bg-teal-700 text-white border-teal-700" : "bg-white text-slate-700 border-slate-200 hover:border-teal-500"}`} data-testid={`${name}-${o}`}>{o}</button>
        ))}
      </div>
    </div>
  );
}
